/**
 * ARTMiE on-site activity tracker — the contact-level counterpart to amh-reco-track.js.
 *
 * The reco tracker learns what a SESSION did; this one learns what a PERSON did, so a customer's
 * browsing accumulates across visits the way it did in Ecomail. Identity arrives three ways:
 *
 *   1. `amh.nid` in the URL of a newsletter click-through — an opaque token the Hub issued and can
 *      verify. Remembered in a first-party cookie, so every later visit from this browser is still
 *      attributed. This is the equivalent of Ecomail's `ecmid`, minus the address in the URL.
 *   2. The signed-in customer's address, which the theme passes in as AMH_TRACK.email.
 *   3. The address the order was placed with, read on the thank-you page.
 *
 * Sent: product and collection views, searches (with how many hits came back), clicks on a search
 * result (with its rank), add-to-cart and checkout starts. NOT every page view — the noise costs
 * more than it tells us.
 *
 * CONSENT-GATED exactly like the reco tracker: nothing is sent, and nothing is queued past the
 * decision, unless analytics consent is given. The address never travels in a URL or a cookie.
 *
 * Config: window.AMH_TRACK = { market, apiBase, brand, email } — market/apiBase fall back to
 * window.AMH_CART_RECO so a theme that already has the reco widget needs no second config block.
 */
(function () {
  "use strict";
  var CFG = window.AMH_TRACK || {};
  var REC = window.AMH_CART_RECO || {};
  var MARKET = String(CFG.market || REC.market || "").trim().toLowerCase();
  var API = String(CFG.apiBase || REC.apiBase || "").replace(/\/+$/, "");
  var BRAND = String(CFG.brand || "artmie").trim().toLowerCase();
  if (!MARKET || !API) return;

  /* ── session id: the SAME one the reco tracker uses, so both streams describe one visit ── */
  var _sid = null;
  function sid() {
    if (_sid) return _sid;
    try {
      var k = "amh_sid", v = localStorage.getItem(k);
      if (!v) { v = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(k, v); }
      _sid = v;
    } catch (e) { _sid = "s_" + Math.random().toString(36).slice(2); }
    return _sid;
  }

  /* ── identity token ─────────────────────────────────────────────────────────────────────── */
  function cookie(name, value, days) {
    try {
      if (value === undefined) {
        var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
        return m ? decodeURIComponent(m[1]) : null;
      }
      // Chrome caps cookie lifetime at 400 days regardless of what we ask for.
      document.cookie = name + "=" + encodeURIComponent(value) + ";path=/;max-age=" + (days * 86400) + ";SameSite=Lax";
    } catch (e) {}
    return null;
  }
  /* Our own visitor id, and the one identity hangs on. `amh_sid` is shared localStorage that four
     theme scripts create with different generators, so on a first visit the last writer wins and
     the id can change part-way through — five events under one id and the next three under another,
     measured on the PL pilot. A cookie we alone write does not move. */
  function visitorId() {
    var v = cookie("amh_vid");
    if (!v) { v = "v_" + Math.random().toString(36).slice(2) + Date.now().toString(36); cookie("amh_vid", v, 365); }
    return v;
  }

  var qs = new URLSearchParams(location.search);
  var fromUrl = !!(qs.get("amh.nid") || qs.get("amh_nid"));
  var nid = qs.get("amh.nid") || qs.get("amh_nid") || cookie("amh_nid");
  var nlc = qs.get("amh.nlc") || qs.get("amh_nlc") || cookie("amh_nlc");
  // Cookies are WRITTEN only once consent is known — an analytics cookie set on arrival, before
  // the banner is answered, is exactly what consent rules exist to stop. Reading one that is
  // already there is free, so a returning visitor is still recognised.
  function persistIds() {
    if (fromUrl && nid) { cookie("amh_nid", nid, 365); if (nlc) cookie("amh_nlc", nlc, 365); }
    visitorId();
  }

  /* ── consent gate (identical to amh-reco-track.js) ──────────────────────────────────────── */
  /* An address the browser can see: the signed-in customer, or the one the order was placed with.
     Carried ON the event rather than announced separately, so identifying somebody never invents a
     page view they did not make. The server treats it as a claim, not proof. */
  var IDENT = {};
  try {
    var ck = window.Shopify && window.Shopify.checkout;
    if (ck && ck.email) IDENT = { email: String(ck.email), source: "order" };
    else if (CFG.email) IDENT = { email: String(CFG.email), source: "account" };
  } catch (e) { if (CFG.email) IDENT = { email: String(CFG.email), source: "account" }; }

  /* ── where this visit came from ─────────────────────────────────────────────────────────────
     Until now a row said WHAT someone opened but never HOW they got there, so a dead address could
     be measured and not explained. Two separate things are captured, because they answer different
     questions: the referrer of THIS page (which is what names the source of a 404 landing), and the
     campaign that started the SESSION (which survives a page or two of browsing).
     Our own hosts span two brands and fifteen markets, so "internal" is decided on the name, not on
     an exact match with this one. */
  var OURS = /(?:^|\.)(?:artmie|papilora)\.[a-z.]{2,}$/i;
  var REF = (function () {
    try {
      if (!document.referrer) return null;
      var u = new URL(document.referrer);
      // The referrer's OWN query string is another site's business — dropped before it leaves here,
      // so a search someone ran elsewhere never reaches our database.
      return { url: (u.protocol + "//" + u.host + u.pathname).slice(0, 300), internal: OURS.test(u.hostname) };
    } catch (e) { return null; }
  })();
  var UTM = (function () {
    var K = "amh_utm";
    try {
      var p = new URLSearchParams(location.search);
      var fresh = { s: p.get("utm_source"), m: p.get("utm_medium"), c: p.get("utm_campaign") };
      if (fresh.s || fresh.m || fresh.c) {
        try { sessionStorage.setItem(K, JSON.stringify(fresh)); } catch (e) {}
        return fresh;
      }
      return JSON.parse(sessionStorage.getItem(K) || "null");
    } catch (e) { return null; }
  })();

  var _consent = null, _queue = [];
  function _post(payload) {
    try {
      var body = JSON.stringify(Object.assign({
        brand: BRAND, market: MARKET, session: sid(), visitor: visitorId(),
        nid: nid || undefined, nlc: nlc || undefined,
        referrer: REF ? REF.url : undefined,
        internal: REF ? REF.internal : undefined,
        utmSource: (UTM && UTM.s) || undefined,
        utmMedium: (UTM && UTM.m) || undefined,
        utmCampaign: (UTM && UTM.c) || undefined,
      }, IDENT, payload));
      var url = API + "/api/public/activity";
      // text/plain keeps this CORS-simple (no preflight); the server parses JSON regardless.
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }))) return;
      fetch(url, { method: "POST", headers: { "Content-Type": "text/plain" }, body: body, keepalive: true }).catch(function () {});
    } catch (e) { /* never break the storefront */ }
  }
  function _flush() {
    if (_consent) { persistIds(); while (_queue.length) _post(_queue.shift()); }
    else { _queue.length = 0; }
  }
  function send(payload) { if (_consent === true) { _post(payload); return; } if (_consent === false) return; _queue.push(payload); }
  function _fallback() {
    try {
      if (typeof window.__artmieConsent === "function") return !!window.__artmieConsent("analytics");
      if (window.__artmieConsent && typeof window.__artmieConsent === "object") return !!window.__artmieConsent.analytics;
      if (document.cookie.indexOf("artmie_consent=analytics") !== -1 || document.cookie.indexOf("artmie_consent=all") !== -1) return true;
    } catch (e) {}
    return false;
  }
  (function initConsent() {
    try {
      var sp = window.Shopify && window.Shopify.customerPrivacy;
      if (sp && typeof sp.analyticsProcessingAllowed === "function") { _consent = !!sp.analyticsProcessingAllowed(); _flush(); return; }
      if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function () {
          var s = window.Shopify && window.Shopify.customerPrivacy;
          _consent = !!(s && s.analyticsProcessingAllowed && s.analyticsProcessingAllowed()) || (!s && _fallback());
          _flush();
          try { if (s && s.subscribe) s.subscribe("visitorConsentCollected", function (ev) { _consent = !!(ev && ev.analyticsAllowed); _flush(); }); } catch (e) {}
        });
        return;
      }
    } catch (e) {}
    _consent = _fallback(); _flush();
  })();

  /* ── what page is this ──────────────────────────────────────────────────────────────────── */
  function meta() { try { return (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {}; } catch (e) { return {}; } }
  function pageType() {
    var m = meta();
    var t = String((m.page && m.page.pageType) || "").toLowerCase();
    // Shopify calls the vendor listing its own type — "vendors", not "collection". Our newsletters
    // link to exactly that page (/collections/vendors?q=Schmincke), so treating only "collection"
    // as a listing threw away the clicks the campaign cared about most.
    if (t === "vendors" || t === "collection") return "collection";
    if (t) return t;
    if (/\/products\//.test(location.pathname)) return "product";
    if (/\/collections\//.test(location.pathname)) return "collection";
    if (/\/search/.test(location.pathname)) return "search";
    return "";
  }
  function handleFromPath(kind) {
    var m = location.pathname.match(new RegExp("/" + kind + "/([^/?#]+)"));
    return m ? decodeURIComponent(m[1]) : null;
  }
  var title = (document.title || "").slice(0, 300);

  /* ── page-level events ──────────────────────────────────────────────────────────────────── */
  var type = pageType();
  if (type === "product") {
    send({ type: "product", handle: handleFromPath("products"), title: title });
  } else if (type === "collection") {
    // On the vendor listing the handle is literally "vendors" for every brand, so the brand itself
    // — which is the whole point of the page — lives in ?q.
    var vendor = /\/collections\/vendors/.test(location.pathname) ? qs.get("q") : null;
    send({ type: "collection", handle: vendor ? "vendor:" + vendor.toLowerCase() : handleFromPath("collections"), title: title });
  }
  /* Searches are deliberately NOT recorded here. amh-search.js already reports every query, its hit
     count, the clicked result and its rank to the Hub as SearchEvent — 566,000 of them since July,
     with zero-result and filter events this widget never saw. It keys them on the same amh_sid we
     send, so joining search history to a contact needs no second copy of the data, and a second
     copy would only disagree with the first. */

  /* Thank-you page. The address the order was actually placed with is the one worth having — it is
     often not the address the newsletter went to, and binding it here back-fills the browsing that
     led to the purchase. */
  try {
    var ord = window.Shopify && window.Shopify.checkout;
    if (ord && ord.email) {
      send({ type: "order", value: Number(ord.total_price) || undefined, currency: ord.currency || undefined });
    }
  } catch (e) {}

  /* ── cart and checkout ──────────────────────────────────────────────────────────────────── */
  /* The cart token is the bridge across checkout. Shopify's checkout runs on its own domain and
     executes nothing of ours, so the address typed there can never reach us from the browser —
     but the order webhook quotes this same token back to the Hub, which turns the order into the
     browser that filled the cart. Fetched once, lazily, only when there is something to say. */
  var _cartToken = null, _cartAsked = false;
  function withCart(payload, done) {
    if (_cartToken || _cartAsked) { payload.cartToken = _cartToken || undefined; done(payload); return; }
    _cartAsked = true;
    try {
      fetch("/cart.js", { credentials: "same-origin" })
        .then(function (r) { return r.json(); })
        // /cart.js hands back "<token>?key=<32 hex>"; the order and checkout webhooks quote only
        // the token. Keeping the key meant not one of 1,441 carts ever matched a webhook.
        .then(function (c) { _cartToken = (c && c.token) ? String(c.token).split("?")[0] : null; payload.cartToken = _cartToken || undefined; done(payload); })
        .catch(function () { done(payload); });
    } catch (e) { done(payload); }
  }

  // Themes add to cart through /cart/add.js; wrapping fetch catches every variant of the button.
  try {
    var _fetch = window.fetch;
    if (_fetch) {
      window.fetch = function (input, init) {
        var url = "";
        try { url = typeof input === "string" ? input : (input && input.url) || ""; } catch (e) {}
        var res = _fetch.apply(this, arguments);
        if (/\/cart\/add/.test(url)) {
          try {
            res.then(function (r) {
              try {
                r.clone().json().then(function (j) {
                  var it = (j && j.items && j.items[0]) || j || {};
                  withCart({ type: "cart_add", handle: it.handle || null, title: it.product_title || null, value: typeof it.final_line_price === "number" ? it.final_line_price / 100 : undefined }, send);
                }).catch(function () {});
              } catch (e) {}
            }).catch(function () {});
          } catch (e) {}
        }
        return res;
      };
    }
  } catch (e) {}

  document.addEventListener("click", function (ev) {
    var el = ev.target && ev.target.closest && ev.target.closest('[name="checkout"], a[href*="/checkout"]');
    if (el) withCart({ type: "checkout_start" }, send);
  }, true);
})();
